import Tooltip from '@mui/material/Tooltip'
import { useEffect, useState } from 'react'

import {
  CaseHistoryEvent,
  CaseHistoryResponse,
  fetchCaseHistory,
} from '../services/CopCaseDetails.service'
import { formatLondonTooltip } from '../utils/londonTooltip'

export type CaseHistoryTabProps = {

  caseId?: string
}

const PLACEHOLDER = '-'

const formatHistoryDateTime = (iso?: string | null): string => {
  if (!iso) {
    return PLACEHOLDER
  }
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return PLACEHOLDER
  }
  const month = date.getMonth() + 1
  const day = date.getDate()
  const year = String(date.getFullYear()).slice(-2)
  let hours = date.getHours()
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const ampm = hours >= 12 ? 'PM' : 'AM'
  hours = hours % 12 || 12
  return `${String(month).padStart(2, '0')}/${day}/${year}, ${hours}:${minutes} ${ampm}`
}

const ACTIVITY_TYPE_LABELS: Record<string, string> = {
  CreateCase: 'Case Created',
  SubmitCase: 'Case Submitted',
  EditCase: 'Case Edited',
  ChangeField: 'Case Edited',
  CreateFile: 'Attachment Added',
  DeleteFile: 'Attachment Removed',
  CreateComment: 'Comment Added',
  CloseCase: 'Case Closed',
  ReopenCase: 'Case Reopened',
}

const humanizeActivityType = (activityType?: string | null): string => {
  if (!activityType) {
    return PLACEHOLDER
  }
  const mapped = ACTIVITY_TYPE_LABELS[activityType]
  if (mapped) {
    return mapped
  }

  return activityType.replace(/([a-z])([A-Z])/g, '$1 $2').trim()
}

const ATTACHMENT_TYPES = new Set(['CreateFile', 'DeleteFile'])

const DateTextTooltip = ({ value, tooltip }: { value: string; tooltip?: string }) => {
  if (!tooltip || !value || value === PLACEHOLDER) return <>{value}</>

  return (
    <Tooltip
      title={tooltip}
      placement="top"
      arrow
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: '#fff',
            color: '#1a202c',
            fontSize: '13px',
            fontWeight: 600,
            padding: '8px 12px',
            borderRadius: '4px',
            boxShadow: '0 1px 6px rgb(0 0 0 / 30%)',
            whiteSpace: 'nowrap',
            maxWidth: 'none',
          },
        },
        arrow: { sx: { color: '#fff' } },
      }}
    >
      <span>{value}</span>
    </Tooltip>
  )
}

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
)

const AttachmentIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.41 17.41a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
)

const getActivityIcon = (activityType?: string | null): JSX.Element => {
  if (activityType && ATTACHMENT_TYPES.has(activityType)) {
    return <AttachmentIcon />
  }
  return <EditIcon />
}

const extractEvents = (response: CaseHistoryResponse | null): CaseHistoryEvent[] =>
  response && Array.isArray(response.value) ? response.value : []

export default function CaseHistoryTab({ caseId }: Readonly<CaseHistoryTabProps>) {
  const [events, setEvents] = useState<CaseHistoryEvent[] | null>(null)
  const [loading, setLoading] = useState<boolean>(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)

    Promise.resolve()
      .then(() => fetchCaseHistory(caseId))
      .then(response => {
        if (cancelled) {
          return
        }
        setEvents(extractEvents(response))
        setLoading(false)
      })
      .catch(() =>{
        if (cancelled) {
          return
        }
        setEvents([])
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [caseId])

  if (loading) {
    return (
      <div className="cop-case-details__panel">
        <p className="cop-case-details__placeholder">Loading history…</p>
      </div>
    )
  }

  if (!events || events.length === 0) {
    return (
      <div className="cop-case-details__panel">
        <p className="cop-case-details__placeholder">
          No history yet{caseId ? ` for case ${caseId}` : ''}.
        </p>
      </div>
    )
  }

  return (
    <div className="cop-case-details__panel">
      <ul className="cop-case-history">
        {events.map((event, index) => {
          const title = humanizeActivityType(event.activityType)
          const message = humanizeActivityType(event.message)
          const dateText = formatHistoryDateTime(event.date)
          const tooltipValue = formatLondonTooltip(event.date)
          const user = event.user && event.user.trim() !== '' ? event.user : ''
          const key = event.id && event.id !== '-'
            ? event.id
            : `${event.activityType ?? 'event'}-${event.date ?? index}-${index}`
          return (
            <li className="cop-case-history__item" key={key}>
              <div className="cop-case-history__icon" aria-hidden="true">
                {getActivityIcon(event.activityType)}
              </div>
              <div className="cop-case-history__content">
                <p className="cop-case-history__title">{title}</p>
                <p className="cop-case-history__meta">
                  <DateTextTooltip value={dateText} tooltip={tooltipValue} />
                  {user ? ` by ${user}` : ''}
                </p>
                {message && message !== PLACEHOLDER && message !== title && (
                  <p className="cop-case-history__title">{message}</p>
                )}
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}